import * as React from "react";
import { createPortal } from "react-dom";
import { Button, cn } from "@finster/ui";
import { ArrowLeft, ExternalLink, Loader2, Search } from "lucide-react";
import {
  filterSkillsByQuery,
  filterSkillsBySource,
  SKILL_SOURCE_BUCKETS,
} from "../catalog";
import { useModalFocus } from "../hooks/useModalFocus";
import {
  type SkillDensity,
  type SkillSourceFilterValue,
  type SkillSummary,
  type SkillViewStatus,
} from "../types";
import { SkillRow } from "./SkillRow";
import { SkillSourceFilter } from "./SkillSourceFilter";

export interface SkillExploreViewProps {
  open: boolean;
  skills: readonly SkillSummary[];
  search: string;
  sourceFilter: SkillSourceFilterValue;
  manageSkillsUrl?: string;
  status?: SkillViewStatus;
  density?: SkillDensity;
  errorMessage?: string;
  onSearchChange?: (value: string) => void;
  onSourceFilterChange?: (value: SkillSourceFilterValue) => void;
  onOpenDetail: (skill: SkillSummary) => void;
  onBack?: () => void;
  onClose: () => void;
  scrollTop?: number;
  onScrollTopChange?: (scrollTop: number) => void;
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  className?: string;
}

export const SkillExploreView: React.FC<SkillExploreViewProps> = (props) => {
  const {
    open,
    skills,
    search,
    sourceFilter,
    manageSkillsUrl,
    status = "ready",
    density = "default",
    errorMessage = "Unable to load Skills.",
    onSearchChange,
    onSourceFilterChange,
    onOpenDetail,
    onBack,
    onClose,
    scrollTop,
    onScrollTopChange,
    returnFocusRef,
    className,
  } = props;
  const dialogRef = React.useRef<HTMLElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const savedScrollTopRef = React.useRef(0);
  const titleId = `skills-title-${React.useId().replace(/:/g, "")}`;
  const visibleSkills = filterSkillsByQuery(filterSkillsBySource(skills, sourceFilter), search);

  useModalFocus({
    open,
    dialogRef,
    onDismiss: onClose,
    returnFocusRef,
  });

  React.useLayoutEffect(() => {
    if (!open || !listRef.current) return;
    listRef.current.scrollTop = scrollTop ?? savedScrollTopRef.current;
  }, [open, scrollTop, status]);

  if (!open) return null;

  const renderList = () => {
    if (status === "idle" || status === "loading") {
      return (
        <div className="flex min-h-0 flex-1 items-center justify-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          Loading Skills…
        </div>
      );
    }
    if (status === "error") {
      return (
        <div
          role="alert"
          className="flex min-h-0 flex-1 items-center justify-center px-4 text-center text-xs text-destructive"
        >
          {errorMessage}
        </div>
      );
    }
    if (visibleSkills.length === 0) {
      const message =
        skills.length === 0
          ? "No Skills are available."
          : search.trim().length === 0 && sourceFilter !== "all"
            ? `No Skills in ${SKILL_SOURCE_BUCKETS[sourceFilter].label}.`
            : "No Skills match your search.";
      return (
        <div className="flex min-h-0 flex-1 items-center justify-center px-4 text-center text-xs text-muted-foreground">
          {message}
        </div>
      );
    }
    return (
      <ul
        ref={listRef}
        // WebKit drops implicit list semantics once list-style is none, which
        // would cost VoiceOver the label and the item count in Mac Office.
        role="list"
        aria-label="Browse Skills"
        className="m-0 flex min-h-0 flex-1 list-none flex-col gap-1 overflow-y-auto p-2"
        onScroll={(event) => {
          savedScrollTopRef.current = event.currentTarget.scrollTop;
          onScrollTopChange?.(event.currentTarget.scrollTop);
        }}
      >
        {visibleSkills.map((skill) => (
          <li key={skill.id}>
            <SkillRow
              skill={skill}
              density={density}
              disabled={!skill.enabled}
              disabledReason={!skill.enabled ? "This Skill is unavailable." : undefined}
              onSelect={onOpenDetail}
            />
          </li>
        ))}
      </ul>
    );
  };

  return createPortal(
    <div className="fixed inset-0 z-[2147483646]">
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          "absolute inset-0 flex min-h-0 flex-col overflow-hidden bg-background text-foreground outline-none",
          className
        )}
      >
        <header className="flex h-13 min-h-13 items-center gap-2 border-b border-border px-4 py-2">
          {onBack && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={onBack}
              aria-label="Back"
              className="size-8 shrink-0 cursor-pointer rounded-lg"
            >
              <ArrowLeft className="size-4" aria-hidden />
            </Button>
          )}
          <h2 id={titleId} className="m-0 min-w-0 flex-1 text-sm font-medium">
            Skills
          </h2>
          {manageSkillsUrl && (
            <a
              href={manageSkillsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus"
            >
              Manage Skills
              <ExternalLink className="size-4" aria-hidden />
            </a>
          )}
        </header>

        <div className="flex h-13 min-h-13 items-center gap-2 border-b border-border px-4 py-2">
          <label className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-lg border border-border bg-background px-2 shadow-xs focus-within:border-primary focus-within:ring-1 focus-within:ring-primary-muted">
            <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="sr-only">Search Skills</span>
            <input
              data-autofocus
              type="search"
              value={search}
              onChange={(event) => onSearchChange?.(event.target.value)}
              placeholder="Search skills"
              className="h-full min-w-0 flex-1 border-0 bg-transparent p-0 text-xs text-foreground outline-none placeholder:text-muted-foreground"
            />
          </label>
          <SkillSourceFilter
            value={sourceFilter}
            onChange={(nextValue) => onSourceFilterChange?.(nextValue)}
            density={density}
            disabled={status === "loading"}
          />
        </div>

        {renderList()}
      </section>
    </div>,
    document.body
  );
};
